import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Chart from 'chart.js/auto';
import { mapArrayToObjectReducer, groupBy } from '@awp/helpers/Array';

import {
  buildFrostProbeGraphData,
  buildFrostProbeOptions,
  calculateFrostProbeStartDate,
} from '@awp/components/Graphs/Common/FrostProbeHelper';

import { selectData, selectPrecisionResistorObservation } from '@stores/dataVisualizationSlice';
import { getFrostProbeDatasets, defaultBuildOptions } from '../SingleStation/Helper';

import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';

import { useFrostProbeContext } from './FrostProbeContext';
import { setAllFilteredWeatherStations, setSensorType } from '@stores/weatherStationSlice';
import { selectSensorTypeByName } from '@stores/dropdownsSlice';
import { FROST_PROBE_CONFIG, REST_API, STATE_STATUS } from '@awp/constants';
import {
  fetchSensorEquipmentPacket,
  getCurrentSensorEquipmentSetFrostProbe,
  setStatus,
} from '@stores/sensorEquipmentPacketSlice';
import { GetJson } from '@awp/helpers/fetchReact';
import { selectSoilTypes } from '@stores/soilTypesSlice';
import { loadImage } from '@awp/helpers/UtilsTypescript';
import annotationPlugin from 'chartjs-plugin-annotation';
import { isValueEmpty } from '@awp/helpers/utilsReact';

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

const SOIL_LAYERS_PADDING = 100;

const FrostProbeGraph = () => {
  const { dataRange, observationTime, weatherStationId } = useFrostProbeContext();

  const graphId = 'frost_probe';
  const showInvalidData = false;
  const endDate = dataRange.endDate;

  const data = useSelector(selectData);
  const precisionResistorObservation = useSelector(selectPrecisionResistorObservation);
  const frostProbe = useSelector(selectSensorTypeByName(FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE));
  const sensorEquipmentSetFrostProbe = useSelector(getCurrentSensorEquipmentSetFrostProbe);
  const soilTypes = useSelector(selectSoilTypes);
  const dispatch = useDispatch();

  const [frostProbeDatasets, setFrostProbeDatasets] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [graphOptions, setGraphOptions] = useState([defaultBuildOptions]);
  const [soilLayers, setSoilLayers] = useState([]);

  const canvasRef = useRef(null);
  const [canvasHeight, setCanvasHeight] = useState(0); // Set initial height to 0 until calculated

  // Function to update the canvas height based on width
  const updateCanvasHeight = () => {
    if (canvasRef.current) {
      const width = canvasRef.current.offsetWidth;
      setCanvasHeight(width * 0.67); // Calculate height as 67% of width
    }
  };

  useEffect(() => {
    return function cleanup() {
      dispatch(setSensorType(null));
      dispatch(setAllFilteredWeatherStations());
    };
  }, []);

  useEffect(() => {
    dispatch(setStatus(STATE_STATUS.IDLE));
    dispatch(fetchSensorEquipmentPacket(weatherStationId));
  }, [weatherStationId]);

  const generateSoilLayerImages = (chart) => {
    const ctx = chart.ctx;
    const soilLayers = chart.options.soilLayers;
    const xEndPixels = chart.scales.xAxis?.getPixelForValue(chart.scales.xAxis.end);
    const yStart = chart.scales.yAxis?.start;

    const reversedSoilLayers = [...(soilLayers ?? [])].reverse();
    reversedSoilLayers.forEach((soilLayer, index) => {
      if (isValueEmpty(soilLayer.soilType.image)) return; // Skip if no image is available

      let currentDepth = index === 0 ? yStart : reversedSoilLayers[index - 1]?.TopmostRelativeElevation;
      currentDepth = currentDepth < yStart ? yStart : currentDepth;
      const currentDepthPixels = chart.scales.yAxis.getPixelForValue(currentDepth);
      const topmostRelativeElevation = soilLayer.TopmostRelativeElevation;
      const topmostRelativeElevationPixels = chart.scales.yAxis.getPixelForValue(soilLayer.TopmostRelativeElevation);

      if (topmostRelativeElevation < yStart) return;

      let yHeightPixels = topmostRelativeElevationPixels - currentDepthPixels;
      yHeightPixels = Math.abs(yHeightPixels) * -1;

      ctx.save();

      ctx.beginPath();
      ctx.translate(xEndPixels, currentDepthPixels);
      ctx.rect(0, 0, SOIL_LAYERS_PADDING, yHeightPixels);
      ctx.closePath();
      ctx.fillStyle = ctx.createPattern(soilLayer.soilType.image, 'repeat');
      ctx.fill();

      ctx.restore();
    });
  };

  const generateSoilLayerLabels = () => {
    const chart = Chart.getChart(graphId);
    const annotations = [];
    const soilLayers = chart.options.soilLayers;
    const yStart = chart.scales.yAxis?.start;

    const reversedSoilLayers = [...(soilLayers ?? [])].reverse();
    reversedSoilLayers?.forEach((soilLayer, index) => {
      let previousSoilLayer = index === 0 ? yStart : reversedSoilLayers[index - 1]?.TopmostRelativeElevation;
      previousSoilLayer = previousSoilLayer < yStart ? yStart : previousSoilLayer;
      const topmostRelativeElevation = soilLayer.TopmostRelativeElevation;
      const difference = topmostRelativeElevation - previousSoilLayer;
      const yValue = topmostRelativeElevation - difference / 2;

      if (topmostRelativeElevation < yStart) return;

      annotations.push({
        type: 'label',
        xValue: chart.scales.xAxis.end,
        xAdjust: SOIL_LAYERS_PADDING / 2,
        yValue,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        content: soilLayer.soilType.Code,
        padding: 3,
        color: 'white',
      });
    });
    chart.options.plugins.annotation.annotations = annotations;

    chart.update();
  };

  useEffect(async () => {
    if (!sensorEquipmentSetFrostProbe || !soilTypes) return;

    // Fetch soil profile data
    const url = `${REST_API.PLACEMENT_SOIL_LAYER}${sensorEquipmentSetFrostProbe.SensorPlacementId}`;
    const response = await GetJson(url);
    const records = await response.json();

    const mySoilLayers = records
      ?.sort((a, b) => Math.abs(a.TopmostRelativeElevation) - Math.abs(b.TopmostRelativeElevation))
      ?.map(async (sl) => {
        const soilType = soilTypes?.find((st) => {
          return st.Id === sl.SoilTypeId;
        });

        return {
          ...sl,
          soilType: soilType
            ? {
                ...soilType,
                image: soilType.PatternImage
                  ? await loadImage(`data:image/jpeg;base64,${soilType.PatternImage}`)
                  : null,
              }
            : null,
        };
      });

    Promise.all(mySoilLayers)
      .then((allSoilLayers) => {
        setSoilLayers(allSoilLayers);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [sensorEquipmentSetFrostProbe, soilTypes]);

  useEffect(() => {
    dispatch(setSensorType(frostProbe));
    dispatch(setAllFilteredWeatherStations());
  }, [dispatch, frostProbe]);

  // Update canvas height on mount and resize
  useEffect(() => {
    updateCanvasHeight();
    window.addEventListener('resize', updateCanvasHeight);
    return () => {
      window.removeEventListener('resize', updateCanvasHeight);
    };
  }, []);

  useMemo(() => {
    if (!data) return;
    const fp = getFrostProbeDatasets(data.Datasets, precisionResistorObservation);

    setFrostProbeDatasets(fp);
  }, [data, showInvalidData]);

  useEffect(() => {
    if (!frostProbeDatasets) return;

    const startDate = calculateFrostProbeStartDate(endDate, dataRange.displayRange);

    //this is an array of layernumber & elevation, we use this later to determine
    // if any layers are missing and fill them so they show as gaps on the graph
    const layers = frostProbeDatasets
      .map((ds) => {
        return {
          layerNumber: ds.SensorLayerNumber,
          elevation: ds.elevation,
        };
      })
      .reduce((acc, cur) => acc.concat(cur), [])
      .filter((val, idx, self) => self.indexOf(val) === idx);

    // take the observation data and add in the elevation, filter by the observation time
    // and finally group the results by TimeStamp
    const filteredFPData = Object.keys(frostProbeDatasets)
      .map((k) => {
        const result = [];
        //check if the dataset has data
        if (frostProbeDatasets[k].Data) {
          frostProbeDatasets[k].Data.filter((v) => new Date(parseInt(v.x)).getHours() == observationTime).forEach(
            (v) => {
              const value = v.Value === 'NAN' ? null : v.Value;
              const y = v.y === 'NAN' ? null : v.y;
              result.push({
                ...v,
                value: value,
                y: y,
                elevation: frostProbeDatasets[k].RelativeElevation,
                sensorLayerNumber: frostProbeDatasets[k].SensorLayerNumber,
              });
            },
          );
        }
        return [k, groupBy(result, 'x')];
      })
      .reduce(mapArrayToObjectReducer, {});

    // transpose our data into it's final form
    // "<timestamp>": { <observation data> }
    let frostProbeObservations = {};

    Object.keys(filteredFPData).map((k) => {
      Object.keys(filteredFPData[k]).map((y) => {
        frostProbeObservations[y] = frostProbeObservations[y] ?? [];
        frostProbeObservations[y].push(filteredFPData[k][y][0]);
      });
    });

    //we have our frost probe data transposed, lets look for missing
    // sensor layers
    let missing = null;

    Object.keys(frostProbeObservations).map((k) => {
      missing = layers.filter(
        (o) =>
          !frostProbeObservations[k].reduce((obj, o) => ((obj[o.sensorLayerNumber] = o.sensorLayerNumber), obj), [])[
            o.layerNumber
          ],
      );

      if (missing.length > 0) {
        missing.map((m) => {
          frostProbeObservations[k].push({
            AssociatedY: null,
            IsInvalid: true,
            Value: null,
            elevation: m.elevation,
            sensorLayerNumber: m.layerNumber,
            x: parseInt(k),
            y: null,
            z: null,
          });
        });
      }
    });

    //almost done with our frostprobe data setup, sort by elevation
    Object.keys(frostProbeObservations).map((k) => {
      frostProbeObservations[k].sort((a, b) =>
        Math.abs(a.elevation) < Math.abs(b.elevation) ? 1 : Math.abs(b.elevation) < Math.abs(a.elevation) ? -1 : 0,
      );
    });

    //reduce down only the days we are set to display
    frostProbeObservations = Object.keys(frostProbeObservations)
      .filter((k) => parseInt(k) < endDate && parseInt(k) > startDate)
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: frostProbeObservations[key],
        });
      }, {});

    setGraphData(buildFrostProbeGraphData(frostProbeObservations, showInvalidData, dataRange.displayRange));
    setGraphOptions({
      ...buildFrostProbeOptions(frostProbeObservations, graphId, showInvalidData, observationTime),
      maintainAspectRatio: true,
      layout: {
        padding: {
          right: SOIL_LAYERS_PADDING,
        },
      },
    });
  }, [frostProbeDatasets, endDate, observationTime]);

  // Initialize or update the chart
  useMemo(() => {
    if (!graphOptions || !graphData || canvasHeight === 0) return;
    const canvas = canvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext('2d');
      let chart = Chart.getChart(graphId);

      // Ensure the canvas height is set before rendering the chart
      canvas.height = canvasHeight; // Explicitly set the canvas height

      const plugins = [
        {
          beforeDraw: (chart) => {
            generateSoilLayerImages(chart);
          },
        },
      ];

      if (chart) {
        chart.data.datasets = graphData;
        chart.options = graphOptions;
        chart.plugins = plugins;

        chart.update();
      } else {
        new Chart(ctx, {
          type: 'line', // Or your graph type
          data: {
            datasets: graphData,
          },
          options: graphOptions,
          plugins,
        });
      }
    }
  }, [graphData, graphOptions, canvasHeight, graphId]);

  useEffect(() => {
    if (!graphId || soilLayers.length === 0) return;

    let chart = Chart.getChart(graphId);

    chart.options.soilLayers = soilLayers;

    generateSoilLayerLabels();

    chart.update();
  }, [graphId, soilLayers]);

  return (
    <div>
      <canvas ref={canvasRef} id={graphId} style={{ width: '100%' }} />
    </div>
  );
};

export default FrostProbeGraph;
